import { useCallback } from 'react';
import { getIn } from 'formik';

import { FInput } from '../../../components/baseV2';
import FormFieldReadOnlyValue from './FormFieldReadOnlyValue';
import FormFieldTopLabel from './FormFieldTopLabel';
import { useFormReadOnly } from './formReadOnlyProvider';
import { DISABLE_BROWSER_AUTOFILL } from '../constants';

import type { ChangeEvent, FocusEvent } from 'react';
import type { FormikValues } from 'formik';
import type { FormTextFieldProps } from '../types';

export default function FormTextField<TValues extends FormikValues>({
  formik,
  name,
  label,
  placeholder = `Enter ${label.toLowerCase()}`,
  required = false,
  multiline = false,
  minRows,
  maxLength,
  disabled = false,
  readOnly = false,
  autoComplete = DISABLE_BROWSER_AUTOFILL,
  showFloatingLabel = true,
  showLabelAbove = false,
  showLabelBelow = false,
  showRequiredIndicator,
  sx,
  onBlur,
  onChange,
}: FormTextFieldProps<TValues>) {
  const readOnlyFromContext = useFormReadOnly();
  const isReadOnly = readOnly || readOnlyFromContext;
  const fieldValue = getIn(formik.values, name) ?? '';
  const fieldError = getIn(formik.errors, name);
  const isTouched = Boolean(getIn(formik.touched, name));
  const trimmedLength = String(fieldValue).trim().length;
  const isOverMaxLength = maxLength != null && trimmedLength > maxLength;
  const errorMessage = isTouched || isOverMaxLength ? fieldError?.toString() : undefined;
  // Suppress the redundant "required field" message for empty required fields; the field still
  // highlights via `error` and its required indicator. Other validation messages still show.
  // `maxLength` is a soft limit: typing past it is allowed, the count helper is shown, and the
  // error appears as soon as the trimmed value exceeds the limit.
  const suppressRequiredMessage = required && trimmedLength === 0;
  let helperText: string | undefined = suppressRequiredMessage ? undefined : errorMessage;
  if (maxLength != null && !suppressRequiredMessage) {
    const characterCount = `${trimmedLength}/${maxLength}`;
    helperText = errorMessage ? `${errorMessage} (${characterCount})` : characterCount;
  }
  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      formik.handleBlur(event);
      onBlur?.(name);
    },
    [formik, name, onBlur],
  );
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      formik.handleChange(event);
      onChange?.(name, event.target.value);
    },
    [formik, name, onChange],
  );

  if (isReadOnly) {
    return (
      <FormFieldReadOnlyValue
        label={label}
        minRows={minRows}
        multiline={multiline}
        required={required}
        value={fieldValue}
      />
    );
  }

  const hasExternalLabel = showLabelAbove || showLabelBelow;

  return (
    <>
      {showLabelAbove ? <FormFieldTopLabel htmlFor={name} label={label} /> : null}
      <FInput
        autoComplete={autoComplete}
        disabled={disabled}
        error={Boolean(errorMessage) || isOverMaxLength}
        fullWidth
        helperText={helperText}
        id={hasExternalLabel ? name : undefined}
        inputProps={showFloatingLabel || hasExternalLabel ? undefined : { 'aria-label': label }}
        label={showFloatingLabel ? label : undefined}
        minRows={minRows}
        multiline={multiline}
        name={name}
        onBlur={handleBlur}
        onChange={handleChange}
        placeholder={placeholder}
        required={required}
        showRequiredIndicator={showRequiredIndicator}
        sx={sx}
        value={fieldValue}
      />
      {showLabelBelow ? (
        <FormFieldTopLabel htmlFor={name} label={label} placement="below" sx={{ mt: 0 }} />
      ) : null}
    </>
  );
}
